import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { useStudySettings } from "@/infrastructure/adapters/user-preference/user-preference.hook";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect } from "react";

import { MobileLayout } from "@/components/layout/MobileLayout";
import { AppStoreBadges } from "@/features/auth/components/AppStoreBadges";
import { LoginPromoText } from "@/features/auth/components/LoginPromoText";
import { DashboardContent } from "@/features/dashboard/components/DashboardContent";
import { EncouragementMessage } from "@/features/dashboard/components/EncouragementMessage";
import { StudySettingsContent } from "@/features/dashboard/components/StudySettingsContent";
import { TopAlert } from "@/features/dashboard/components/TopAlert";
import { shouldShowWebLoginUI } from "@/utils/platform";
import { Stack } from "@mui/material";

type TDashboardPageProps = {};

export const DashboardPage: FC<TDashboardPageProps> = () => {
  const queryClient = useQueryClient();
  const { settings, isLoading } = useStudySettings();

  // Re-fetch them once on dashboard mount so the encouragement message reflects
  // the newest isBoostVisible value instead of a stale cached one.
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_PREFERENCE.STUDY_SETTINGS] });
  }, [queryClient]);

  if (isLoading || !settings) {
    return <LoadingScreen />;
  }

  // Show onboarding if weeklyStudyTimeTarget is not a valid value (1–10080 minutes = 1 week max)
  const target = settings.weeklyStudyTimeTarget;
  const isFirstTime = target === null || target === undefined || target <= 0 || target > 10080;

  if (isFirstTime) {
    return (
      <MobileLayout bottomNav={false}>
        <StudySettingsContent />
      </MobileLayout>
    );
  }

  const showWebUI = shouldShowWebLoginUI();

  return (
    <MobileLayout bottomNav={true}>
      <Stack gap={2}>
        <TopAlert />
        <EncouragementMessage />
        {showWebUI && (
          <Stack gap={1}>
            <AppStoreBadges />
            <LoginPromoText />
          </Stack>
        )}
        <DashboardContent />
      </Stack>
    </MobileLayout>
  );
};
